import { DocumentInput, NameInput } from "./general";

export interface PrintConfigPickupLocation {
  value: string;
  label: string;
  _key?: string;
}

export interface PrintConfigProduct {
  value: string;
  label: string;
  type: "ticket" | "image";
  _key?: string;
}

export interface PrintConfig {
  pickupLocations?: PrintConfigPickupLocation[];
  physicalProducts?: PrintConfigProduct[];
  s3Folders?: Record<string, string>;
  pickupText?: string;
  /** Fan print modal fixed strings: English or Spanish. */
  printUiLanguage?: "en" | "es";
  /** Optional override for fan print thank-you pickup lines (My Moments). */
  thankYouPickupInstruction?: string;
}

export interface KeepsakeLabels {
  photoLabel?: string;
  ticketLabel?: string;
  fan1stLabel?: string;
  themeLabel?: string;
  sponsorFrameLabel?: string;
}

/** Optional wording for the three after-download prompts. Blank fields use the built-in copy. */
export interface DownloadPromptCopy {
  purchaseHeadline?: string;
  purchaseBody?: string;
  shareHeadline?: string;
  shareBody?: string;
  storyHeadline?: string;
  storyBody?: string;
}

/** Which primary keepsake types fans can choose on My Moments (all default on when unset). */
export interface KeepsakeOfferings {
  /** Photo / sponsor frame tab */
  frame?: boolean;
  /** Primary commemorative ticket tab */
  ticket?: boolean;
  gif?: boolean;
  /**
   * Photo with scoreboard overlay on My Moments + still download.
   * Default off when unset.
   */
  frameScoreboard?: boolean;
}

/** When showWatermark is on: which unchecked opt-in gates downloads and shows the modal. */
export type WatermarkOptInTrigger = "sponsor" | "team";

export type WatermarkPopupDisplayTiming = "onDownload" | "onPageLoad" | "both";

export type WatermarkFeatureIcon = "watermarkFree" | "digitalDownload" | "fanBenefits";

export interface WatermarkOptInFeature {
  icon: WatermarkFeatureIcon;
  title: string;
  description: string;
}

export interface WatermarkOptInConfig {
  trigger: WatermarkOptInTrigger;
  popupDisplayTiming?: WatermarkPopupDisplayTiming;
  headlineLine1?: string;
  headlineLine1Color?: string;
  headlineLine2?: string;
  headlineLine2Color?: string;
  subheadline?: string;
  subheadlineAccentText?: string;
  subheadlineAccentColor?: string;
  features?: WatermarkOptInFeature[];
  optInCheckboxText?: string;
  optInCheckboxSize?: number;
  privacyPolicyLinkText?: string;
  privacyPolicyUrl?: string;
  primaryButtonText?: string;
  primaryButtonBackgroundColor?: string;
  primaryButtonTextColor?: string;
  secondaryButtonText?: string;
  secondaryButtonBackgroundColor?: string;
  secondaryButtonBorderColor?: string;
  secondaryButtonTextColor?: string;
  backgroundColor?: string;
  bodyTextColor?: string;
  featureTitleColor?: string;
  featureIconColor?: string;
  watermarkLogoOpacity?: number;
  modalTitle?: string;
  modalCheckboxLabel?: string;
  modalConfirmButtonText?: string;
  modalDeclineButtonText?: string;
  textColor?: string;
}

/**
 * Single line of customizable welcome-page copy. The fan client renders the
 * headline ("PHOTOS OF YOU") plus two subheadings; each line is independently
 * toggleable, editable, and lets the operator nudge spacing / font size.
 */
export type WelcomeTextAlign = "left" | "center" | "right";

export type WelcomeContentPlacement = "top" | "center" | "bottom";

export type WelcomeFontFamily = "oswald" | "poppins" | "sfpro";

export type WelcomeImageZone = "top" | "above-text" | "below-text" | "bottom";

export interface WelcomeTextLine {
  text?: string;
  enabled?: boolean;
  marginTop?: number;
  fontSize?: number;
  color?: string;
  textAlign?: WelcomeTextAlign;
  fontFamily?: WelcomeFontFamily;
  fontWeight?: number;
}

export interface WelcomePageLayoutConfig {
  enabled?: boolean;
  textAlign?: WelcomeTextAlign;
  contentPlacement?: WelcomeContentPlacement;
  textBlockPaddingTop?: number;
  textBlockPaddingBottom?: number;
  showMomentoTopLogo?: boolean;
  momentoTopLogoScalePercent?: number;
  momentoTopLogoZone?: WelcomeImageZone;
  middleLogoScalePercent?: number;
  middleLogoZone?: WelcomeImageZone;
  /** Vertical offset for team center logo (px); negative moves toward top. */
  middleLogoOffsetYPx?: number;
  showSponsorImage?: boolean;
  sponsorMaxWidthPercent?: number;
  sponsorMaxHeightPercent?: number;
  sponsorImageZone?: WelcomeImageZone;
  /** Padding above sponsor image (px), e.g. below subheading 2. */
  sponsorPaddingTopPx?: number;
  /** When true with a center graphic uploaded, hide text/logos and show only the graphic. */
  centerGraphicMode?: boolean;
  centerGraphicScalePercent?: number;
  /** Vertical offset for center graphic (px); negative moves toward top. */
  centerGraphicOffsetYPx?: number;
  /** Horizontal offset for center graphic (px); negative moves left. */
  centerGraphicOffsetXPx?: number;
  ctaButtonText?: string;
  /** Padding above the fixed CTA button (px). */
  ctaPaddingTop?: number;
  /** Extra gap between stacked content and the CTA footer (px). */
  contentClearanceAboveCtaPx?: number;
  /** Vertical shift for CTA within footer (px); positive moves toward opt-out. */
  ctaVerticalOffsetPx?: number;
  /** Gap between CTA and opt-out button (px). */
  ctaGapAboveOptOutPx?: number;
  backgroundOverlayOpacity?: number;
  headingUseGradient?: boolean;
  preheading?: WelcomeTextLine;
}

export interface WelcomeTextConfig {
  heading?: WelcomeTextLine;
  subheading1?: WelcomeTextLine;
  subheading2?: WelcomeTextLine;
  layout?: WelcomePageLayoutConfig;
}

/** Fan welcome age-gate copy. Empty fields fall back to 18+/21+ defaults. */
export type WelcomeAgeGateMinAge = 18 | 21;
/** Calendar date order for this team. US = MM/DD/YYYY, EU = DD/MM/YYYY. */
export type TeamRegion = "us" | "eu";
export interface WelcomeAgeGateCopy {
  heading?: string;
  body?: string;
  confirmText?: string;
}

/** Fan Under 18 Flow copy. Empty fields fall back to defaults. */
export interface Under18FlowCopy {
  heading?: string;
  body?: string;
  under14Error?: string;
}

/** One card on the fan “Where were you sitting?” step (GA vs normal seat). */
export interface EnhancedGeneralAdmissionChoiceCard {
  title?: string;
  subtitle?: string;
  /** S3 key; signed URL added when serving games / team to clients. */
  imageKey?: string;
  /** Resolved signed URL (API responses only; not persisted from fan). */
  image?: string;
  /** Optional bullet lines on the card; GA may use subsection names. */
  bulletLines?: string[];
  /** Optional #RRGGBB border around the card on the fan picker. */
  borderColor?: string;
}

/** Normalized 0–1 rectangle on a subsection image (fractions of width / height). */
export interface EnhancedGeneralAdmissionMapRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Four corner points (normalized 0–1), in order around the perimeter. */
export type EnhancedGeneralAdmissionMapQuad = [
  [number, number],
  [number, number],
  [number, number],
  [number, number],
];

/** Maps a region on the area card or location map to one section list index. */
export interface EnhancedGeneralAdmissionMapRegion {
  /** Index into `sectionNumbers` / `seatingSectionNames`. */
  sectionIndex: number;
  rect?: EnhancedGeneralAdmissionMapRect;
  quad?: EnhancedGeneralAdmissionMapQuad;
}

/**
 * One bleacher / GA subsection: area picker card + location map +
 * mapping to real seating section names.
 */
export interface EnhancedGeneralAdmissionSubsection {
  /** Display name (e.g. “Left Field Bleachers”). */
  displayName?: string;
  /** Shown under title on area card, e.g. “Sections 301–305”. */
  sectionRangeLabel?: string;
  areaCardImageKey?: string;
  locationMapImageKey?: string;
  /** Resolved signed URLs (API responses only). */
  areaCardImage?: string;
  locationMapImage?: string;
  /** Labels shown on the map strip (e.g. 301, 302). */
  sectionNumbers?: string[];
  /** Same length as sectionNumbers; names must match game seating special sections. */
  seatingSectionNames?: string[];
  /** Optional CSS hex for card border on the area step. */
  cardBorderColor?: string;
  /** Optional tap targets on the area card image (normalized rects). */
  areaCardRegions?: EnhancedGeneralAdmissionMapRegion[];
  /** Optional tap targets on the location map (normalized rects). */
  locationMapRegions?: EnhancedGeneralAdmissionMapRegion[];
}

/**
 * Operator-configurable enhanced GA fan journey (Cubs-style). Default off per team.
 */
export interface EnhancedGeneralAdmissionTeamSettings {
  enabled?: boolean;
  generalAdmissionCard?: EnhancedGeneralAdmissionChoiceCard;
  normalSeatCard?: EnhancedGeneralAdmissionChoiceCard;
  subsections?: EnhancedGeneralAdmissionSubsection[];
  /** Fan “Where were you sitting?” step — main heading (optional). */
  whereStepHeadline?: string;
  /** Fan “Where were you sitting?” step — supporting line under the heading (optional). */
  whereStepSubhead?: string;
  /** Fan “Which area?” step — main heading (optional). */
  areaStepHeadline?: string;
  /**
   * Fan “Which area?” step — text under the heading. Use `{title}` for the GA card title
   * (e.g. Wrigley Field Bleachers). Optional.
   */
  areaStepSubheadTemplate?: string;
}

/** Operator-customizable fan page when a game has no published moments yet. */
export interface PreMomentSignupSettings {
  sectionTitle?: string;
  bodyParagraph1?: string;
  bodyParagraph2?: string;
  graphicImageKey?: string;
  graphicImage?: string;
  notifyTitle?: string;
  notifyDescription?: string;
  submitButtonText?: string;
}

/** Fan My Moments: which keepsake view loads first for this team’s games. */
export type MyMomentsDefaultKeepsake = "image" | "gif" | "ticket" | "framescoreboard";

export interface TeamSecondaryLogoColorDuoEntry {
  id: string;
  name: string;
  logoKey: string;
  logo?: string;
  /** #RGB or #RRGGBB — team1 primary when this duo is selected on a game. */
  primaryColor: string;
}

/** Operator “Commercial / operations” tab — selectable services for the team. */
export type TeamEnabledServiceKey =
  | "fanExperience"
  | "crowdAnalytics"
  | "snaps"
  | "snapsSquad"
  | "snapsKiosk"
  | "printTable"
  | "attentionTracking"
  | "tagboard"
  | "suitesPrintingExperience"
  | "groupsSthPrint";

export const TEAM_ENABLED_SERVICE_KEYS: TeamEnabledServiceKey[] = [
  "fanExperience",
  "crowdAnalytics",
  "snaps",
  "snapsSquad",
  "snapsKiosk",
  "printTable",
  "attentionTracking",
  "tagboard",
  "suitesPrintingExperience",
  "groupsSthPrint",
];

export interface UserDetailsConfig {
  showFirstName?: boolean;
  showLastName?: boolean;
  showEmail?: boolean;
  showPhoneField?: boolean;
  showCountryField?: boolean;
  showZipCodeField?: boolean;
  showDateOfBirth?: boolean;
  showSeasonTicketHolder?: boolean;
  /** Fan user details: show Canadian resident toggle under zip code. Default off. */
  canadaCheck?: boolean;
  /** Fan zip field: label Zip Code/Postal Code, alphanumeric, min 6 chars required. Default off. */
  zipPostalCodeMode?: boolean;
}

export interface OptInTextLink {
  text: string;
  url: string;
  _key?: string;
}

/** One stacked consent / T&C paragraph (user details), with optional links and suffix. */
export interface OptInConsentLine {
  text: string;
  links?: OptInTextLink[];
  suffix?: string;
  /** Client-side stable key for list rendering; not persisted. */
  _lineKey?: string;
}

export interface OptInTextWithLinks {
  text: string;
  links?: OptInTextLink[];
  suffix?: string;
  defaultChecked?: boolean;
  /** When true, fan opt-in checkbox starts checked, is locked, and cannot be unchecked. */
  required?: boolean;
  /** When true, fan may uncheck but submit shows requiredPopupText until they opt in. */
  requiredWithPopup?: boolean;
  /** Modal message when fan submits with opt-in unchecked (requiredWithPopup). */
  requiredPopupText?: string;
  /** Multiple paragraphs shown above the Next button; rendered in order when set. */
  lines?: OptInConsentLine[];
}

export interface AddTeamRequest {
  name: string;
  league: string[];
  city: string;
  stadium: string;
  hostname?: string;
  /** US or EU date format for fans and operator game dates. Default us. */
  teamRegion?: TeamRegion;
  /** Email domain for staff (e.g. seattlekraken.com); Profiles toggle filters by *@domain. */
  staffEmailDomain?: string;
  /** Team abbreviation shown in operator contexts (exactly 3 letters). */
  teamAbbreviation?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  /** Games page card: home logo area background; omit to use primaryColor. */
  gamesCardLogoBackgroundColor?: string;
  hidePurchase?: string | boolean;
  /** After first My Moments image/ticket save, show keepsake store popup. Default off. */
  purchasePopupAfterDownloadedImage?: string | boolean;
  /** After first photo save in a visit, show SHARE THE MOMENT. Default off. */
  fanSharingPromptEnabled?: string | boolean;
  /** After first photo save in a visit, show TELL US YOUR GAMEDAY STORY. Default off. */
  gamedayStoryPromptEnabled?: string | boolean;
  /** Unlock Camera Controls Data Image Mode (red/green capture latch). Default off. */
  dataImageModeEnabled?: string | boolean;
  /** Seconds between automated Trigger All while capturing. 0 = manual only. */
  dataImageAutoTriggerSeconds?: number;
  hideDownload?: string | boolean;
  showOrderPrintedPhoto?: string | boolean;
  showWatermark?: string | boolean;
  watermarkOptInConfig?: WatermarkOptInConfig;
  /** Fan Moment Scoreboard (crop editor, My Moments header, download toggle).
   *  Super-admin; default on. */
  momentScoreboardFanEnabled?: string | boolean;
  momentScoreboardDefaultOnPhoto?: string | boolean;
  momentScoreboardBackgroundColor?: string;
  momentScoreboardDescriptionColor?: string;
  /** Hide home team primary logo on moment scoreboard header. Default off (logo shown). */
  momentScoreboardHideTeam1Logo?: string | boolean;
  momentScoreboardIncludeTeamScores?: string | boolean;
  gamesCardIncludeTeamScores?: string | boolean;
  /** Home (team1) on top of game cards and left on scoreboard. Default off. */
  homeTeamOnTop?: string | boolean;
  /** Fan client GA4/dataLayer analytics tracking. Default on. */
  analyticsTrackingEnabled?: string | boolean;
  analyticsPopupConfig?: import("../../utils/analyticsPopupConfig").AnalyticsPopupConfig;
  cookiePolicyText?: string;
  termsOfUseText?: string;
  privacyPolicyText?: string;
  /** Operator: auto-run Crowd Analytics when a moment moves Draft→Data. Default off. */
  automatedDataRuns?: string | boolean;
  enablePaidDownload?: string | boolean;
  profileEnabled?: boolean;
  isMomentoClient?: boolean;
  /** Token for public engagement dashboard (no login). */
  publicEngagementToken?: string | null;
  /** Public engagement dashboard refresh: auto (every minute) or manual (operator button). */
  publicEngagementRefreshMode?: "auto" | "manual";
  /** Last operator-triggered refresh timestamp for manual mode. */
  publicEngagementRefreshAt?: number;
  /** Which game-by-game breakdown columns appear on Engagement page vs public dashboard. */
  engagementBreakdownColumns?:
    import("./engagementBreakdownColumns").EngagementBreakdownColumnsConfig;
  /** Display names for promotion categories on the Engagement performance board only. */
  engagementPromotionLeaderboardLabels?: Record<string, string>;
  /** Public engagement link layout: graph, logos, promotions, top metric cards. */
  publicEngagementDashboardConfig?:
    import("./publicEngagementDashboardConfig").PublicEngagementDashboardConfig;
  weatherDisplayConfig?: import("./weatherDisplayConfig").WeatherDisplayConfig;
  /** When true, Game / Momento recap includes Snaps print counts and pinned photos. */
  showSnapsOnGameRecap?: boolean;
  /** When true, Game / Momento recap Snaps section includes QR scan totals. */
  showSnapsQrScansOnGameRecap?: boolean;
  /** When true, Game / Momento recap Snaps section includes scans/prints conversion %. */
  showSnapsConversionOnGameRecap?: boolean;
  sendToVideoboard?: boolean;
  defaultSeatingConfiguration?: string; // default seating configuration for home games
  defaultTicketTemplate?: string;
  defaultFan1stTicketTemplate?: string;
  defaultSpecialSectionTicketTemplate?: string;
  /** Saved overlay positions/fonts for ticket templates (not tied to a specific template image). */
  defaultTicketPlacement?: import("./game").TicketTemplatePlacement;
  defaultSponsorOverlay?: string;
  /** Sponsor frames hidden from games that have not finished. */
  sponsorFramesHiddenFromUnfinished?: string[];
  myMomentsDefaultKeepsake?: MyMomentsDefaultKeepsake;
  keepsakeLabels?: KeepsakeLabels;
  keepsakeOfferings?: KeepsakeOfferings;
  printConfig?: PrintConfig;
  userDetailsConfig?: UserDetailsConfig;
  customTeamOptInText?: OptInTextWithLinks;
  customSponsorOptInText?: OptInTextWithLinks;
  customThirdOptInText?: OptInTextWithLinks;
  /** FanID page checkbox copy (Safeguards). Empty = hidden. Same shape as third T&C. */
  customFanIdOptInText?: OptInTextWithLinks;
  customConsentText?: OptInTextWithLinks;
  shareHashtag?: string;
  downloadPromptCopy?: DownloadPromptCopy;
  fanSharingPromptImageKey?: string;
  gamedayStoryPromptImageKey?: string;
  fanSharingPromptImage?: string;
  gamedayStoryPromptImage?: string;
  /** Labels for Data moment short description dropdown in Operator (max 25 chars each). */
  dataMomentCaptions?: string[];
  /** Operator notes shown with the data moment script on the Moments list. */
  dataMomentInformation?: string;
  /** Max standard (assigned) seat entry attempts per session on fan client. Default 5. */
  seatEntryLimitRegular?: number;
  /** Max GA / special-section searches per session. Default 5. */
  seatEntryLimitGA?: number;
  /** Blur opted-out seat areas on published fan momento images. Default off. */
  optOutBlurEnabled?: boolean;
  /** Opt-out blur strength 0–100%. Default 50. */
  optOutBlurStrengthPercent?: number;
  /** Opt-out blur mask: circle (default), box, or vertical rectangle. */
  optOutBlurShape?: "circle" | "box" | "verticalRectangle";
  /** Pixels added to each section seat radius for opt-out blur. */
  optOutBlurRadiusOffsetPixels?: number;
  /** Corner radius for box and vertical-rectangle blur. 0 keeps square corners. */
  optOutBlurCornerRadiusPixels?: number;
  /** Fan Surname verification after seat selection. Default off. */
  surnameVerificationEnabled?: boolean;
  /** Hide Adjust Photo on the fan My Moments experience. Default off. */
  disableAdjustPhoto?: boolean;
  /** After welcome CTA, require DOB meeting welcomeAgeGateMinAge. Default off. */
  welcomeAgeGateEnabled?: boolean;
  /** Minimum age for the welcome age gate. Default 21. */
  welcomeAgeGateMinAge?: WelcomeAgeGateMinAge;
  /** Optional custom age-gate heading / body / checkbox copy. */
  welcomeAgeGateCopy?: WelcomeAgeGateCopy;
  /**
   * Under 18 Flow (Safeguards). Default off. Age gate on welcome (min 14);
   * 14–17 must verify surname and a match removes that seat from this game's
   * Opt-Out list. 18+ is standard. Wins over welcomeAgeGateEnabled when both on.
   */
  under18FlowEnabled?: boolean;
  /** Optional custom Under 18 Flow heading / body / under-14 error. */
  under18FlowCopy?: Under18FlowCopy;
  /** Customize welcome Opt-Out button size. Default off. */
  optOutButtonCustomSizeEnabled?: boolean;
  /** Welcome Opt-Out size as % of default (200 = double). Used when custom size is on. */
  optOutButtonSizePercent?: number;
  /** Optional home-page Opt-Out button label. Blank uses the default. */
  optOutButtonText?: string;
  /** When on, fan side menu includes Opt Out (opens opt-out page with this team). Default off. */
  menuBarOptOutEnabled?: boolean;
  /** When on, fans can opt a seat out of the entire season. Default off. */
  seasonLongOptOutEnabled?: boolean;
  /**
   * When on, operators must set Sitting/Standing on each moment before publish;
   * Standing applies standingBlurShiftPixels to opt-out blur.
   */
  sitStandShiftingEnabled?: boolean;
  /**
   * Vertical blur shift for Standing moments (px). Positive = up, negative = down.
   */
  standingBlurShiftPixels?: number;
  mainContactName?: string;
  mainContactEmail?: string;
  mainContactJobTitle?: string;
  mainContactDepartment?: string;
  secondaryContactName?: string;
  secondaryContactEmail?: string;
  secondaryContactJobTitle?: string;
  secondaryContactDepartment?: string;
  gamedayContactName?: string;
  gamedayContactEmail?: string;
  gamedayContactPhone?: string;
  gamedayContactJobTitle?: string;
  gamedayContactDepartment?: string;
  operationsName?: string;
  operationsEmail?: string;
  operationsPhone?: string;
  operationsJobTitle?: string;
  operationsDepartment?: string;
  primaryTicketProvider?: string;
  clientTypeSiTickets?: boolean;
  primarySponsor?: string;
  primarySponsorContactName?: string;
  primarySponsorContactEmail?: string;
  primarySponsorContactPhone?: string;
  primarySponsorContactJobTitle?: string;
  primarySponsorContactDepartment?: string;
  dataLeadContactName?: string;
  dataLeadContactEmail?: string;
  dataLeadContactPhone?: string;
  dataLeadContactJobTitle?: string;
  dataLeadContactDepartment?: string;
  teamGoogleSheetUrl?: string;
  /** GroupMe chat URL for operators on this team. */
  groupMeOperatorUrl?: string;
  invoicesDueNotes?: string;
  annualLicenseFee?: string;
  operatorInstructions?: {
    operators?: string;
    operatorLocation?: string;
    operatorLocationImageKey?: string;
    operatorLocationImageUrl?: string;
    credentialInformation?: string;
    credentialPickupLocation?: string;
    credentialPickupLocationImageKey?: string;
    credentialPickupLocationImageUrl?: string;
    parkingInformation?: string;
    parkingLocation?: string;
    parkingLocationImageKey?: string;
    parkingLocationImageUrl?: string;
    arrivalTime?: string;
    attire?: string;
    credentialSubmissionLink?: string;
    snapSquadLocation?: string;
    snapSquadLocationImageKey?: string;
    snapSquadLocationImageUrl?: string;
    marketingBoothLocation?: string;
    marketingBoothLocationImageKey?: string;
    marketingBoothLocationImageUrl?: string;
    customFields?: Array<{ key: string; label: string; value: string }>;
  };
  /** Minimum Instax print order quantity for Snaps Tracker charts. */
  instaxMoq?: number | null;
  /** How many SnapSquad / Booth assignment slots this client needs on a home game. */
  staffingNeeds?: import("./teamStaffingNeeds").TeamStaffingNeeds | null;
  contractEndDateMs?: number | null;
  teamEnabledServices?: TeamEnabledServiceKey[];
  /** When true, team appears in Operator Client Tracker. */
  clientTrackerEnabled?: boolean;
  primaryClientServicesRepEmail?: string | null;
  supportingClientServicesRepEmails?: string[];
  clientTrackerManualStatusOverride?: string | null;
  clientTrackerLastContactAt?: number | null;
  /** Regional fan hostname: after welcome, fans pick a club (/teams). */
  fanWelcomeRequiresClubPicker?: boolean;
  /** Collect fan details before game selection (lead gen first flow). */
  leadGenFirst?: boolean;
  /** Show this club on the multi-club fan /teams picker. */
  includeInFanHubPicker?: boolean;
  /** Optional enhanced GA fan flow (Wrigley-style). */
  enhancedGeneralAdmission?: EnhancedGeneralAdmissionTeamSettings;
  preMomentSignup?: PreMomentSignupSettings;
  secondaryLogoColorDuos?: TeamSecondaryLogoColorDuoEntry[];
}

export interface TeamInput extends AddTeamRequest {
  logoKey: string;
  paywallImageKey?: string;
  ticketAdImageKey?: string;
  popupImageKey?: string;
  // Step 3
  welcomeBackgroundImageKey?: string;
  welcomeMiddleLogoImageKey?: string;
  welcomeSponsorImageKey?: string;
  welcomeCenterGraphicImageKey?: string;
  photosTextColor?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  /** Operator-editable welcome page copy / spacing / per-line toggles. */
  welcomeTextConfig?: WelcomeTextConfig;
  // End Step 3
}

export interface UpdateTeamRequest {
  name?: string;
  league?: string[];
  city?: string;
  stadium?: string;
  logoKey: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  gamesCardLogoBackgroundColor?: string;
  paywallImageKey?: string;
  ticketAdImageKey?: string;
  popupImageKey?: string;
  // Step 3
  welcomeBackgroundImageKey?: string;
  welcomeMiddleLogoImageKey?: string;
  welcomeSponsorImageKey?: string;
  welcomeCenterGraphicImageKey?: string;
  photosTextColor?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  welcomeTextConfig?: WelcomeTextConfig;
  // End Step 3
  hidePurchase?: string | boolean;
  /** After first My Moments image/ticket save, show keepsake store popup. Default off. */
  purchasePopupAfterDownloadedImage?: string | boolean;
  /** After first photo save in a visit, show SHARE THE MOMENT. Default off. */
  fanSharingPromptEnabled?: string | boolean;
  /** After first photo save in a visit, show TELL US YOUR GAMEDAY STORY. Default off. */
  gamedayStoryPromptEnabled?: string | boolean;
  /** Unlock Camera Controls Data Image Mode (red/green capture latch). Default off. */
  dataImageModeEnabled?: string | boolean;
  /** Seconds between automated Trigger All while capturing. 0 = manual only. */
  dataImageAutoTriggerSeconds?: number;
  hideDownload?: string | boolean;
  showOrderPrintedPhoto?: string | boolean;
  showWatermark?: string | boolean;
  momentScoreboardFanEnabled?: string | boolean;
  momentScoreboardDefaultOnPhoto?: string | boolean;
  momentScoreboardBackgroundColor?: string;
  momentScoreboardDescriptionColor?: string;
  /** Hide home team primary logo on moment scoreboard header. Default off (logo shown). */
  momentScoreboardHideTeam1Logo?: string | boolean;
  momentScoreboardIncludeTeamScores?: string | boolean;
  gamesCardIncludeTeamScores?: string | boolean;
  /** Home (team1) on top of game cards and left on scoreboard. Default off. */
  homeTeamOnTop?: string | boolean;
  analyticsTrackingEnabled?: string | boolean;
  analyticsPopupConfig?: import("../../utils/analyticsPopupConfig").AnalyticsPopupConfig;
  cookiePolicyText?: string;
  termsOfUseText?: string;
  privacyPolicyText?: string;
  /** Operator: auto-run Crowd Analytics when a moment moves Draft→Data. Default off. */
  automatedDataRuns?: string | boolean;
  enablePaidDownload?: string | boolean;
  profileEnabled?: boolean;
  isMomentoClient?: boolean;
  /** Token for public engagement dashboard (no login). */
  publicEngagementToken?: string | null;
  /** Public engagement dashboard refresh: auto (every minute) or manual (operator button). */
  publicEngagementRefreshMode?: "auto" | "manual";
  /** Last operator-triggered refresh timestamp for manual mode. */
  publicEngagementRefreshAt?: number;
  /** Which game-by-game breakdown columns appear on Engagement page vs public dashboard. */
  engagementBreakdownColumns?:
    import("./engagementBreakdownColumns").EngagementBreakdownColumnsConfig;
  /** Display names for promotion categories on the Engagement performance board only. */
  engagementPromotionLeaderboardLabels?: Record<string, string>;
  /** Public engagement link layout: graph, logos, promotions, top metric cards. */
  publicEngagementDashboardConfig?:
    import("./publicEngagementDashboardConfig").PublicEngagementDashboardConfig;
  weatherDisplayConfig?: import("./weatherDisplayConfig").WeatherDisplayConfig;
  /** When true, Game / Momento recap includes Snaps print counts and pinned photos. */
  showSnapsOnGameRecap?: boolean;
  /** When true, Game / Momento recap Snaps section includes QR scan totals. */
  showSnapsQrScansOnGameRecap?: boolean;
  /** When true, Game / Momento recap Snaps section includes scans/prints conversion %. */
  showSnapsConversionOnGameRecap?: boolean;
  sendToVideoboard?: boolean;
  hostname?: string;
  /** US or EU date format for fans and operator game dates. Default us. */
  teamRegion?: TeamRegion;
  staffEmailDomain?: string;
  teamAbbreviation?: string;
  defaultSeatingConfiguration?: string; // default seating configuration for home games
  defaultTicketTemplate?: string;
  defaultFan1stTicketTemplate?: string;
  defaultSpecialSectionTicketTemplate?: string;
  /** Saved overlay positions/fonts for ticket templates (not tied to a specific template image). */
  defaultTicketPlacement?: import("./game").TicketTemplatePlacement;
  defaultSponsorOverlay?: string;
  /** Sponsor frames hidden from games that have not finished. */
  sponsorFramesHiddenFromUnfinished?: string[];
  myMomentsDefaultKeepsake?: MyMomentsDefaultKeepsake;
  keepsakeLabels?: KeepsakeLabels;
  keepsakeOfferings?: KeepsakeOfferings;
  printConfig?: PrintConfig;
  userDetailsConfig?: UserDetailsConfig;
  customTeamOptInText?: OptInTextWithLinks;
  customSponsorOptInText?: OptInTextWithLinks;
  customThirdOptInText?: OptInTextWithLinks;
  /** FanID page checkbox copy (Safeguards). Empty = hidden. Same shape as third T&C. */
  customFanIdOptInText?: OptInTextWithLinks;
  customConsentText?: OptInTextWithLinks;
  shareHashtag?: string;
  downloadPromptCopy?: DownloadPromptCopy;
  fanSharingPromptImageKey?: string;
  gamedayStoryPromptImageKey?: string;
  fanSharingPromptImage?: string;
  gamedayStoryPromptImage?: string;
  dataMomentCaptions?: string[];
  /** Operator notes shown with the data moment script on the Moments list. */
  dataMomentInformation?: string;
  seatEntryLimitRegular?: number;
  seatEntryLimitGA?: number;
  optOutBlurEnabled?: boolean;
  optOutBlurStrengthPercent?: number;
  optOutBlurShape?: "circle" | "box" | "verticalRectangle";
  /** Pixels added to each section seat radius for opt-out blur. */
  optOutBlurRadiusOffsetPixels?: number;
  /** Corner radius for box and vertical-rectangle blur. 0 keeps square corners. */
  optOutBlurCornerRadiusPixels?: number;
  /** Fan Surname verification after seat selection. Default off. */
  surnameVerificationEnabled?: boolean;
  /** Hide Adjust Photo on the fan My Moments experience. Default off. */
  disableAdjustPhoto?: boolean;
  /** After welcome CTA, require DOB meeting welcomeAgeGateMinAge. Default off. */
  welcomeAgeGateEnabled?: boolean;
  /** Minimum age for the welcome age gate. Default 21. */
  welcomeAgeGateMinAge?: WelcomeAgeGateMinAge;
  /** Optional custom age-gate heading / body / checkbox copy. */
  welcomeAgeGateCopy?: WelcomeAgeGateCopy;
  /**
   * Under 18 Flow (Safeguards). Default off. Age gate on welcome (min 14);
   * 14–17 must verify surname and a match removes that seat from this game's
   * Opt-Out list. 18+ is standard. Wins over welcomeAgeGateEnabled when both on.
   */
  under18FlowEnabled?: boolean;
  /** Optional custom Under 18 Flow heading / body / under-14 error. */
  under18FlowCopy?: Under18FlowCopy;
  /** Customize welcome Opt-Out button size. Default off. */
  optOutButtonCustomSizeEnabled?: boolean;
  /** Welcome Opt-Out size as % of default (200 = double). Used when custom size is on. */
  optOutButtonSizePercent?: number;
  /** Optional home-page Opt-Out button label. Blank uses the default. */
  optOutButtonText?: string;
  /** When on, fan side menu includes Opt Out (opens opt-out page with this team). Default off. */
  menuBarOptOutEnabled?: boolean;
  /** When on, fans can opt a seat out of the entire season. Default off. */
  seasonLongOptOutEnabled?: boolean;
  /**
   * When on, operators must set Sitting/Standing on each moment before publish;
   * Standing applies standingBlurShiftPixels to opt-out blur.
   */
  sitStandShiftingEnabled?: boolean;
  /**
   * Vertical blur shift for Standing moments (px). Positive = up, negative = down.
   */
  standingBlurShiftPixels?: number;
  mainContactName?: string;
  mainContactEmail?: string;
  mainContactJobTitle?: string;
  mainContactDepartment?: string;
  secondaryContactName?: string;
  secondaryContactEmail?: string;
  secondaryContactJobTitle?: string;
  secondaryContactDepartment?: string;
  gamedayContactName?: string;
  gamedayContactEmail?: string;
  gamedayContactPhone?: string;
  gamedayContactJobTitle?: string;
  gamedayContactDepartment?: string;
  operationsName?: string;
  operationsEmail?: string;
  operationsPhone?: string;
  operationsJobTitle?: string;
  operationsDepartment?: string;
  primaryTicketProvider?: string;
  clientTypeSiTickets?: boolean;
  primarySponsor?: string;
  primarySponsorContactName?: string;
  primarySponsorContactEmail?: string;
  primarySponsorContactPhone?: string;
  primarySponsorContactJobTitle?: string;
  primarySponsorContactDepartment?: string;
  dataLeadContactName?: string;
  dataLeadContactEmail?: string;
  dataLeadContactPhone?: string;
  dataLeadContactJobTitle?: string;
  dataLeadContactDepartment?: string;
  teamGoogleSheetUrl?: string;
  /** GroupMe chat URL for operators on this team. */
  groupMeOperatorUrl?: string;
  invoicesDueNotes?: string;
  annualLicenseFee?: string;
  operatorInstructions?: {
    operators?: string;
    operatorLocation?: string;
    operatorLocationImageKey?: string;
    operatorLocationImageUrl?: string;
    credentialInformation?: string;
    credentialPickupLocation?: string;
    credentialPickupLocationImageKey?: string;
    credentialPickupLocationImageUrl?: string;
    parkingInformation?: string;
    parkingLocation?: string;
    parkingLocationImageKey?: string;
    parkingLocationImageUrl?: string;
    arrivalTime?: string;
    attire?: string;
    credentialSubmissionLink?: string;
    snapSquadLocation?: string;
    snapSquadLocationImageKey?: string;
    snapSquadLocationImageUrl?: string;
    marketingBoothLocation?: string;
    marketingBoothLocationImageKey?: string;
    marketingBoothLocationImageUrl?: string;
    customFields?: Array<{ key: string; label: string; value: string }>;
  };
  /** Minimum Instax print order quantity for Snaps Tracker charts. */
  instaxMoq?: number | null;
  /** How many SnapSquad / Booth assignment slots this client needs on a home game. */
  staffingNeeds?: import("./teamStaffingNeeds").TeamStaffingNeeds | null;
  contractEndDateMs?: number | null;
  teamEnabledServices?: TeamEnabledServiceKey[];
  clientTrackerEnabled?: boolean;
  primaryClientServicesRepEmail?: string | null;
  supportingClientServicesRepEmails?: string[];
  clientTrackerManualStatusOverride?: string | null;
  clientTrackerLastContactAt?: number | null;
  fanWelcomeRequiresClubPicker?: boolean;
  leadGenFirst?: boolean;
  includeInFanHubPicker?: boolean;
  enhancedGeneralAdmission?: EnhancedGeneralAdmissionTeamSettings;
  preMomentSignup?: PreMomentSignupSettings;
}

export interface TeamPopulated extends DocumentInput {
  name: string;
  league: NameInput[];
  city: NameInput;
  sport: NameInput;
  state: NameInput;
  stadium: NameInput;
  logo?: string;
  logoKey: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  gamesCardLogoBackgroundColor?: string;
  paywallImage?: string;
  paywallImageKey?: string;
  ticketAdImage?: string;
  ticketAdImageKey?: string;
  popupImage?: string;
  popupImageKey?: string;
  // Step 3
  welcomeBackgroundImage?: string;
  welcomeBackgroundImageKey?: string;
  welcomeMiddleLogoImage?: string;
  welcomeMiddleLogoImageKey?: string;
  welcomeSponsorImage?: string;
  welcomeSponsorImageKey?: string;
  welcomeCenterGraphicImage?: string;
  welcomeCenterGraphicImageKey?: string;
  photosTextColor?: string;
  buttonBgColor?: string;
  buttonTextColor?: string;
  /** Operator-editable welcome page copy / spacing / per-line toggles. */
  welcomeTextConfig?: WelcomeTextConfig;
  hidePurchase?: string | boolean;
  /** After first My Moments image/ticket save, show keepsake store popup. Default off. */
  purchasePopupAfterDownloadedImage?: string | boolean;
  /** After first photo save in a visit, show SHARE THE MOMENT. Default off. */
  fanSharingPromptEnabled?: string | boolean;
  /** After first photo save in a visit, show TELL US YOUR GAMEDAY STORY. Default off. */
  gamedayStoryPromptEnabled?: string | boolean;
  /** Unlock Camera Controls Data Image Mode (red/green capture latch). Default off. */
  dataImageModeEnabled?: string | boolean;
  /** Seconds between automated Trigger All while capturing. 0 = manual only. */
  dataImageAutoTriggerSeconds?: number;
  hideDownload?: string | boolean;
  showOrderPrintedPhoto?: string | boolean;
  showWatermark?: string | boolean;
  watermarkOptInConfig?: WatermarkOptInConfig;
  /** Fan Moment Scoreboard (crop editor, My Moments header, download toggle).
   *  Super-admin; default on. */
  momentScoreboardFanEnabled?: string | boolean;
  momentScoreboardDefaultOnPhoto?: string | boolean;
  momentScoreboardBackgroundColor?: string;
  momentScoreboardDescriptionColor?: string;
  /** Hide home team primary logo on moment scoreboard header. Default off (logo shown). */
  momentScoreboardHideTeam1Logo?: string | boolean;
  momentScoreboardIncludeTeamScores?: string | boolean;
  gamesCardIncludeTeamScores?: string | boolean;
  /** Home (team1) on top of game cards and left on scoreboard. Default off. */
  homeTeamOnTop?: string | boolean;
  /** Fan client GA4/dataLayer analytics tracking. Default on. */
  analyticsTrackingEnabled?: string | boolean;
  analyticsPopupConfig?: import("../../utils/analyticsPopupConfig").AnalyticsPopupConfig;
  cookiePolicyText?: string;
  termsOfUseText?: string;
  privacyPolicyText?: string;
  /** Operator: auto-run Crowd Analytics when a moment moves Draft→Data. Default off. */
  automatedDataRuns?: string | boolean;
  enablePaidDownload?: string | boolean;
  profileEnabled?: boolean;
  isMomentoClient?: boolean;
  /** Token for public engagement dashboard (no login). */
  publicEngagementToken?: string | null;
  /** Public engagement dashboard refresh: auto (every minute) or manual (operator button). */
  publicEngagementRefreshMode?: "auto" | "manual";
  /** Last operator-triggered refresh timestamp for manual mode. */
  publicEngagementRefreshAt?: number;
  /** Which game-by-game breakdown columns appear on Engagement page vs public dashboard. */
  engagementBreakdownColumns?:
    import("./engagementBreakdownColumns").EngagementBreakdownColumnsConfig;
  /** Display names for promotion categories on the Engagement performance board only. */
  engagementPromotionLeaderboardLabels?: Record<string, string>;
  /** Public engagement link layout: graph, logos, promotions, top metric cards. */
  publicEngagementDashboardConfig?:
    import("./publicEngagementDashboardConfig").PublicEngagementDashboardConfig;
  weatherDisplayConfig?: import("./weatherDisplayConfig").WeatherDisplayConfig;
  /** When true, Game / Momento recap includes Snaps print counts and pinned photos. */
  showSnapsOnGameRecap?: boolean;
  /** When true, Game / Momento recap Snaps section includes QR scan totals. */
  showSnapsQrScansOnGameRecap?: boolean;
  /** When true, Game / Momento recap Snaps section includes scans/prints conversion %. */
  showSnapsConversionOnGameRecap?: boolean;
  sendToVideoboard?: boolean;
  hostname?: string;
  /** US or EU date format for fans and operator game dates. Default us. */
  teamRegion?: TeamRegion;
  staffEmailDomain?: string;
  teamAbbreviation?: string;
  defaultSeatingConfiguration?: string;
  defaultTicketTemplate?: string;
  defaultFan1stTicketTemplate?: string;
  defaultSpecialSectionTicketTemplate?: string;
  /** Saved overlay positions/fonts for ticket templates (not tied to a specific template image). */
  defaultTicketPlacement?: import("./game").TicketTemplatePlacement;
  defaultSponsorOverlay?: string;
  /** Sponsor frames hidden from games that have not finished. */
  sponsorFramesHiddenFromUnfinished?: string[];
  myMomentsDefaultKeepsake?: MyMomentsDefaultKeepsake;
  keepsakeLabels?: KeepsakeLabels;
  keepsakeOfferings?: KeepsakeOfferings;
  printConfig?: PrintConfig;
  userDetailsConfig?: UserDetailsConfig;
  customTeamOptInText?: OptInTextWithLinks;
  customSponsorOptInText?: OptInTextWithLinks;
  customThirdOptInText?: OptInTextWithLinks;
  /** FanID page checkbox copy (Safeguards). Empty = hidden. Same shape as third T&C. */
  customFanIdOptInText?: OptInTextWithLinks;
  customConsentText?: OptInTextWithLinks;
  shareHashtag?: string;
  downloadPromptCopy?: DownloadPromptCopy;
  fanSharingPromptImageKey?: string;
  gamedayStoryPromptImageKey?: string;
  fanSharingPromptImage?: string;
  gamedayStoryPromptImage?: string;
  dataMomentCaptions?: string[];
  /** Operator notes shown with the data moment script on the Moments list. */
  dataMomentInformation?: string;
  seatEntryLimitRegular?: number;
  seatEntryLimitGA?: number;
  optOutBlurEnabled?: boolean;
  optOutBlurStrengthPercent?: number;
  optOutBlurShape?: "circle" | "box" | "verticalRectangle";
  /** Pixels added to each section seat radius for opt-out blur. */
  optOutBlurRadiusOffsetPixels?: number;
  /** Corner radius for box and vertical-rectangle blur. 0 keeps square corners. */
  optOutBlurCornerRadiusPixels?: number;
  /** Fan Surname verification after seat selection. Default off. */
  surnameVerificationEnabled?: boolean;
  /** Hide Adjust Photo on the fan My Moments experience. Default off. */
  disableAdjustPhoto?: boolean;
  /** After welcome CTA, require DOB meeting welcomeAgeGateMinAge. Default off. */
  welcomeAgeGateEnabled?: boolean;
  /** Minimum age for the welcome age gate. Default 21. */
  welcomeAgeGateMinAge?: WelcomeAgeGateMinAge;
  /** Optional custom age-gate heading / body / checkbox copy. */
  welcomeAgeGateCopy?: WelcomeAgeGateCopy;
  /**
   * Under 18 Flow (Safeguards). Default off. Age gate on welcome (min 14);
   * 14–17 must verify surname and a match removes that seat from this game's
   * Opt-Out list. 18+ is standard. Wins over welcomeAgeGateEnabled when both on.
   */
  under18FlowEnabled?: boolean;
  /** Optional custom Under 18 Flow heading / body / under-14 error. */
  under18FlowCopy?: Under18FlowCopy;
  /** Customize welcome Opt-Out button size. Default off. */
  optOutButtonCustomSizeEnabled?: boolean;
  /** Welcome Opt-Out size as % of default (200 = double). Used when custom size is on. */
  optOutButtonSizePercent?: number;
  /** Optional home-page Opt-Out button label. Blank uses the default. */
  optOutButtonText?: string;
  /** When on, fan side menu includes Opt Out (opens opt-out page with this team). Default off. */
  menuBarOptOutEnabled?: boolean;
  /** When on, fans can opt a seat out of the entire season. Default off. */
  seasonLongOptOutEnabled?: boolean;
  /**
   * When on, operators must set Sitting/Standing on each moment before publish;
   * Standing applies standingBlurShiftPixels to opt-out blur.
   */
  sitStandShiftingEnabled?: boolean;
  /**
   * Vertical blur shift for Standing moments (px). Positive = up, negative = down.
   */
  standingBlurShiftPixels?: number;
  mainContactName?: string;
  mainContactEmail?: string;
  mainContactJobTitle?: string;
  mainContactDepartment?: string;
  secondaryContactName?: string;
  secondaryContactEmail?: string;
  secondaryContactJobTitle?: string;
  secondaryContactDepartment?: string;
  gamedayContactName?: string;
  gamedayContactEmail?: string;
  gamedayContactPhone?: string;
  gamedayContactJobTitle?: string;
  gamedayContactDepartment?: string;
  operationsName?: string;
  operationsEmail?: string;
  operationsPhone?: string;
  operationsJobTitle?: string;
  operationsDepartment?: string;
  primaryTicketProvider?: string;
  clientTypeSiTickets?: boolean;
  primarySponsor?: string;
  primarySponsorContactName?: string;
  primarySponsorContactEmail?: string;
  primarySponsorContactPhone?: string;
  primarySponsorContactJobTitle?: string;
  primarySponsorContactDepartment?: string;
  dataLeadContactName?: string;
  dataLeadContactEmail?: string;
  dataLeadContactPhone?: string;
  dataLeadContactJobTitle?: string;
  dataLeadContactDepartment?: string;
  teamGoogleSheetUrl?: string;
  /** GroupMe chat URL for operators on this team. */
  groupMeOperatorUrl?: string;
  invoicesDueNotes?: string;
  annualLicenseFee?: string;
  operatorInstructions?: {
    operators?: string;
    operatorLocation?: string;
    operatorLocationImageKey?: string;
    operatorLocationImageUrl?: string;
    credentialInformation?: string;
    credentialPickupLocation?: string;
    credentialPickupLocationImageKey?: string;
    credentialPickupLocationImageUrl?: string;
    parkingInformation?: string;
    parkingLocation?: string;
    parkingLocationImageKey?: string;
    parkingLocationImageUrl?: string;
    arrivalTime?: string;
    attire?: string;
    credentialSubmissionLink?: string;
    snapSquadLocation?: string;
    snapSquadLocationImageKey?: string;
    snapSquadLocationImageUrl?: string;
    marketingBoothLocation?: string;
    marketingBoothLocationImageKey?: string;
    marketingBoothLocationImageUrl?: string;
    customFields?: Array<{ key: string; label: string; value: string }>;
  };
  instaxMoq?: number | null;
  /** How many SnapSquad / Booth assignment slots this client needs on a home game. */
  staffingNeeds?: import("./teamStaffingNeeds").TeamStaffingNeeds;
  contractEndDateMs?: number;
  teamEnabledServices?: TeamEnabledServiceKey[];
  clientTrackerEnabled?: boolean;
  primaryClientServicesRepEmail?: string | null;
  supportingClientServicesRepEmails?: string[];
  clientTrackerManualStatusOverride?: string | null;
  clientTrackerLastContactAt?: number | null;
  fanWelcomeRequiresClubPicker?: boolean;
  leadGenFirst?: boolean;
  includeInFanHubPicker?: boolean;
  enhancedGeneralAdmission?: EnhancedGeneralAdmissionTeamSettings;
  preMomentSignup?: PreMomentSignupSettings;
  secondaryLogoColorDuos?: TeamSecondaryLogoColorDuoEntry[];
}

export interface TeamAdsFields {
  adAfterContactInfoKey?: string;
  adAfterContactInfoImage?: string;
  adAfterContactInfoFile?: File;
  adAfterContactInfoLink?: string;
  titleSponsorLogoKey?: string;
  titleSponsorLogoImage?: string;
  titleSponsorLogoFile?: File;
  titleSponsorLogoLink?: string;
  /** Display name for sponsor opt-in campaign metrics (e.g. "CCM"). */
  sponsorName?: string;
  /** Inclusive Central calendar day YYYY-MM-DD when sponsor opt-in tracking begins. */
  sponsorCampaignStartDay?: string;
  /** Optional inclusive Central calendar day YYYY-MM-DD when campaign ends. */
  sponsorCampaignEndDay?: string;
  fullPageAd1Key?: string;
  fullPageAd1Image?: string;
  fullPageAd1File?: File;
  fullPageAd1Link?: string;
  fullPageAd2Key?: string;
  fullPageAd2Image?: string;
  fullPageAd2File?: File;
  fullPageAd2Link?: string;
  fullPageAd3Key?: string;
  fullPageAd3Image?: string;
  fullPageAd3File?: File;
  fullPageAd3Link?: string;
  fullPageAd4Key?: string;
  fullPageAd4Image?: string;
  fullPageAd4File?: File;
  fullPageAd4Link?: string;
  fullPageAd5Key?: string;
  fullPageAd5Image?: string;
  fullPageAd5File?: File;
  fullPageAd5Link?: string;
  fullPageAd6Key?: string;
  fullPageAd6Image?: string;
  fullPageAd6File?: File;
  fullPageAd6Link?: string;
  uploadBannerAd1Key?: string;
  uploadBannerAd1Image?: string;
  uploadBannerAd1Link?: string;
  uploadBannerAd1File?: File;
  uploadBannerAd2Key?: string;
  uploadBannerAd2Image?: string;
  uploadBannerAd2Link?: string;
  uploadBannerAd2File?: File;
  uploadBannerAd3Key?: string;
  uploadBannerAd3Image?: string;
  uploadBannerAd3Link?: string;
  uploadBannerAd3File?: File;
  uploadBannerAd4Key?: string;
  uploadBannerAd4Image?: string;
  uploadBannerAd4Link?: string;
  uploadBannerAd4File?: File;
  uploadBannerAd5Key?: string;
  uploadBannerAd5Image?: string;
  uploadBannerAd5Link?: string;
  uploadBannerAd5File?: File;
}

export interface TeamAdsInput extends TeamAdsFields {
  teamId: string;
}

export interface TeamAdsWithId extends TeamAdsFields {
  _id?: string;
}

export interface GetTeamAdsByTeamRequest {
  teamId: string;

  noImages?: boolean; // set to not load images
  onlySponsorLogoImage?: boolean; // set to load single image
}

export interface AddTeamAdsRequest {
  teamId: string;
}

export interface UpdateTeamAdsRequest {
  teamId: string;

  // use single parameters group:
  // fill parameters to add or replace image
  imageKey?: string;
  teamName?: string;
  imageMimeType?: string;
  filename?: string;

  // fill parameter to delete existing image
  deleteImageKey?: string;

  // fill both parameters to update link string
  imageLinkKey?: string;
  imageLink?: string;

  sponsorName?: string;
  sponsorCampaignStartDay?: string;
  sponsorCampaignEndDay?: string;
  updateSponsorCampaign?: boolean;
}

/** When a configured popup should appear (one per team per value). */
export type TeamPopupOccurrence =
  | "my_moments_page_load"
  | "momento_image_download"
  | "momento_ticket_download"
  | "all_momentos_download"
  | "after_first_moment"
  | "last_image";

export type TeamPopupClickAction =
  | "custom_url"
  | "direct_to_purchase"
  | "feedback"
  | "share_with_friends";

export interface TeamPopupItem {
  popupId: string;
  occurrence: TeamPopupOccurrence;
  clickAction: TeamPopupClickAction;
  customUrl?: string;
  imageKey?: string;
}

export interface TeamPopupItemPopulated extends TeamPopupItem {
  imageUrl?: string;
}

export interface TeamPopupConfigFields {
  teamId: string;
  items: TeamPopupItem[];
}

export interface TeamPopupConfigPopulated {
  _id: string;
  teamId: string;
  items: TeamPopupItemPopulated[];
}

export interface GetTeamPopupsByTeamRequest {
  teamId: string;
  noImages?: boolean;
}

export interface AddTeamPopupsRequest {
  teamId: string;
}

export interface UpdateTeamPopupsItemsRequest {
  items: TeamPopupItem[];
}

export interface UpdateTeamPopupImageRequestBody {
  teamId?: string;
  teamName?: string;
  popupId?: string;
  filename?: string;
  imageMimeType?: string;
  deletePopupId?: string;
}
